import { logger } from '@adornis/base/logging.js';
import { X } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { updateAssignmentProposalMessageMutation } from '../../../db/api/update-assignment-proposal.js';

interface AssignmentCategory {
  label: string;
  key: 'schuelerabgaben' | 'musterloesung' | 'bewertungsschema' | 'leereKlausur';
}

export interface AssignmentConfirmationPanelHandle {
  finalize: () => {
    baseContent: string;
    schuelerabgaben: string[];
    musterloesung?: string;
    bewertungsschema?: string;
    leereKlausur?: string;
  };
}

interface AssignmentConfirmationPanelProps {
  baseContent?: string;
  proposal: {
    schuelerabgaben: string[];
    musterloesung?: string;
    bewertungsschema?: string;
    leereKlausur?: string;
  };
  /** Whether the blank-exam slot applies to this proposal. `false` on re-uploads. */
  allowBlankExam?: boolean;
  locked?: boolean;
  conversationId?: string;
  messageId?: string;
}

export const AssignmentConfirmationPanel = forwardRef<
  AssignmentConfirmationPanelHandle,
  AssignmentConfirmationPanelProps
>(function AssignmentConfirmationPanel(
  { baseContent = '', proposal, allowBlankExam, locked = false, conversationId, messageId },
  ref,
) {
  const { t } = useTranslation();

  const CATEGORIES: AssignmentCategory[] = [
    { label: t('chatMessages.studentSubmissions', { defaultValue: 'Schülerabgaben' }), key: 'schuelerabgaben' },
    { label: t('chatMessages.masterSolution', { defaultValue: 'Musterlösung' }), key: 'musterloesung' },
    { label: t('chatMessages.gradingScheme', { defaultValue: 'Bewertungsschema' }), key: 'bewertungsschema' },
    { label: t('chatMessages.blankExam', { defaultValue: 'Leere Klausur' }), key: 'leereKlausur' },
  ];

  const effectiveShowBlank = allowBlankExam !== false;
  const [assignments, setAssignments] = useState<Record<string, string[]>>(() => {
    const result: Record<string, string[]> = {
      schuelerabgaben: Array.isArray(proposal.schuelerabgaben) ? [...proposal.schuelerabgaben] : [],
      musterloesung: proposal.musterloesung ? [proposal.musterloesung] : [],
      bewertungsschema: proposal.bewertungsschema ? [proposal.bewertungsschema] : [],
      leereKlausur: proposal.leereKlausur ? [proposal.leereKlausur] : [],
    };
    return result;
  });

  const [draggedFile, setDraggedFile] = useState<{ file: string; from: string } | null>(null);

  // Persist assignments to DB whenever they change.
  useEffect(() => {
    const persist = async () => {
      if (locked) return;
      if (!conversationId || !messageId) return;

      try {
        // Do NOT overwrite the visible message content while the proposal is still active.
        // Persist structured assignment fields only; keep the message `content` equal to
        // the original `baseContent` so the UI doesn't render the appended bullets prematurely.
        const contentToPersist = String(baseContent || '').trim();

        await updateAssignmentProposalMessageMutation(
          conversationId,
          messageId,
          contentToPersist,
          assignments.schuelerabgaben || [],
          assignments.musterloesung?.[0] ?? '',
          assignments.bewertungsschema?.[0] ?? '',
          assignments.leereKlausur?.[0] ?? '',
          true,
          '',
        )();
      } catch (err) {
        logger.error(err, `Failed to persist assignment draft for message ${messageId}`);
      }
    };

    persist();
  }, [assignments, conversationId, messageId, baseContent, locked]);

  const handleDragStart = (file: string, category: string) => {
    if (locked) return;
    setDraggedFile({ file, from: category });
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (locked) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (locked) return;
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e: React.DragEvent, toCategory: string) => {
    if (locked) return;
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    if (!draggedFile) return;

    const { file, from } = draggedFile;

    logger.info(`Dragging ${file} from ${from} to ${toCategory}`);

    setAssignments(prev => {
      const newAssignments = { ...prev };

      // Remove from original category
      const fromList = newAssignments[from];
      if (fromList) {
        newAssignments[from] = fromList.filter(f => f !== file);
      }

      // Add to new category
      const toList = newAssignments[toCategory];
      if (toCategory === 'schuelerabgaben') {
        newAssignments[toCategory] = toList ? [...toList, file] : [file];
      } else {
        // For single-file categories, replace
        newAssignments[toCategory] = [file];
      }

      logger.info(`Updated assignments: ${JSON.stringify(newAssignments)}`);
      return newAssignments;
    });

    setDraggedFile(null);
  };

  const removeFile = (file: string, category: string) => {
    if (locked) return;
    setAssignments(prev => {
      const catList = prev[category];
      return {
        ...prev,
        [category]: catList ? catList.filter(f => f !== file) : [],
      };
    });
  };

  useImperativeHandle(ref, () => ({
    finalize: () => {
      logger.info(`Panel.finalize() called with assignments state: ${JSON.stringify(assignments)}`);
      const result: any = {
        baseContent,
        schuelerabgaben: assignments['schuelerabgaben'] || [],
      };

      const musterloesung = assignments['musterloesung'];
      if (musterloesung && musterloesung.length > 0) result.musterloesung = musterloesung[0];

      const bewertungsschema = assignments['bewertungsschema'];
      if (bewertungsschema && bewertungsschema.length > 0) result.bewertungsschema = bewertungsschema[0];

      const leereKlausur = assignments['leereKlausur'];
      if (leereKlausur && leereKlausur.length > 0) result.leereKlausur = leereKlausur[0];

      logger.info(`Panel.finalize() returning: ${JSON.stringify(result)}`);
      return result;
    },
  }));

  return (
    <div className="mt-1 space-y-1.5">
      <div className="grid grid-cols-1 gap-1.5">
        {CATEGORIES.filter(c => c.key !== 'leereKlausur' || effectiveShowBlank).map(category => {
          const categoryFiles = assignments[category.key] || [];
          return (
            <div key={category.key}>
              <div className="mb-0.5 text-[11px] text-slate-700 dark:text-slate-300">
                <span className="font-medium">{category.label}:</span>
              </div>

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, category.key)}
                className="min-h-[28px] rounded border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-secondary px-1.5 py-1 transition-colors hover:border-slate-400 dark:hover:border-slate-500"
              >
                {categoryFiles.length === 0 ? null : (
                  <div className="flex flex-wrap gap-1">
                    {categoryFiles.map((file, idx) => (
                      <div
                        key={idx}
                        draggable={!locked}
                        onDragStart={() => handleDragStart(file, category.key)}
                        className="inline-flex max-w-full items-center gap-1 rounded bg-slate-900 dark:bg-slate-700 px-1.5 py-1 text-[10px] font-medium text-white cursor-move"
                      >
                        <span className="truncate max-w-[220px]">{file}</span>
                        <button
                          onClick={() => removeFile(file, category.key)}
                          disabled={locked}
                          className="text-slate-300 hover:text-red-300 disabled:opacity-40"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
